import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import { CookiesKeys, URLs } from '../../Constants/Consts';
import { fetchApi, splitOnCapitals } from '../../Helpers/Functions';
import SectionHeader from "../../components/CoreComponents/Details/SectionHeader.tsx";
import Loader from '../../components/CoreComponents/Loader';
import FormDDL from '../../components/CoreComponents/ReactFormComponents/FormDDL';
import FormInput from '../../components/CoreComponents/ReactFormComponents/FormInput';
import { addSelectedUser } from "../../store/SelectedUser";
import { setModal, updateDownloadTriggered } from '../../store/Utilities.ts';
import { RootState } from "../../store/store.ts";
import { AssignmentApiData, Project, UserAssignment, getProjectText } from '../../types/types';
import roleCheck from "../../utils/roleCheck.ts";
import { ROLES, ADGroupMapping } from "../../utils/roles.ts";

export default function UserPage() {
    const usersData = useSelector((state: RootState) => state.UserAssignment.Users) as any
    const schema = z.object({
        FullName: z.string().nonempty({ message: "Please enter the full name." }),
        KionEmail: z.string().email({ message: "Please enter a valid email." }),
        LoginId: z.string().email({ message: "Please enter the Login ID." }),
        AccessLevelSelection: z.number().gt(0, "An access level must be selected."),
        ProjectSelection: z.number().gt(0, "A project must be selected."),
        DisciplineSelection: z.number().gt(0, "A discipline must be selected."),
        DD1: z.string()
    }).refine(() => {
        return uuid != null || !usersData.some(user => user.kionEmail?.toLowerCase() === watchEmail?.toLowerCase());
    }, {
        message: "A user with this email already exists.",
        path: ["KionEmail"]
    }).refine(data => {
        return data.DD1.split(',').length < 11;
    }, {
        message: "Please select up to 10 or less sequences or controls to proceed.",
        path: ["DD1"]
    });
    type AssignmentFormType = z.infer<typeof schema>;

    const { uuid } = useParams();
    const navigate = useNavigate();
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = useState<string>();
    const [assignmentData, setAssignmentData] = useState<AssignmentApiData | null>(null);
    const dd1SelectRef = useRef<any>(null);
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        setValue,
        getValues
    } = useForm<AssignmentFormType>({
        resolver: zodResolver(schema),
    });

    const SelectedUser = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const { assignmentUuid: userUuid } = SelectedUser
    const userRole = SelectedUser.role;
    
    // Permission checks:
    // - canEditUserIdentity: SuperAdmin OR users with canModifyRoles flag can edit name, email, login ID, and access level
    // - canEditUserAssignment: SuperAdmin, Admin, AdminManager can edit project, discipline, sequence of other users
    const canEditUserIdentity = roleCheck(userRole, [ROLES.SuperAdmin]) || SelectedUser.canModifyRoles === true;
    const canEditUserAssignment = roleCheck(userRole, [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager]);
    
    // Check if editing own record
    const isEditingOwnRecord = userUuid.toString() === uuid;
    
    // Role-based permissions for editing own record
    // All users can edit their own assignments
    const canEditOwnProject = isEditingOwnRecord;
    const canEditOwnDiscipline = isEditingOwnRecord;
    const canEditOwnSequence = isEditingOwnRecord;
    
    const watchEmail = useWatch({ control, name: "KionEmail" });
    const [isFetchingUserData, setIsFetchingUserData] = useState(false)
    const disciplineSelectionWatchValue = useRef(0)
    const projectSelectionWatchValue = useRef(0)
    const fetchAssignmentData = async () => {
        setIsFetchingUserData(true)
        try {
            // Force a fresh read: the assignment record can be mutated by a prior save (PUT),
            // and a browser-cached GET would show the previous project/discipline selection.
            const response = await fetchApi(URLs.getAssignmentUrl(uuid), { cache: 'no-store' });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json() as AssignmentApiData;
            projectSelectionWatchValue.current = data.projectNumber
            disciplineSelectionWatchValue.current = data.disciplineId
            setAssignmentData(data);
            resetAssignmentData(data);
        } catch (error) {
            setSaveFailedErrorMessage('An error occurred while loading the user assignment');
        } finally {
            setIsFetchingUserData(false)
        }
    };

    const createAssignmentData = () => {
        const data = {
            fullName: "",
            kionEmail: "",
            loginId: "",
            accessLevelId: 0,
            projectNumber: 0,
            disciplineId: 0,
            sequence: ""
        } as AssignmentApiData;
        setAssignmentData(data);
        resetAssignmentData(data);
    };

    const resetAssignmentData = (data: AssignmentApiData) => {
        reset({
            FullName: data.fullName,
            KionEmail: data.kionEmail,
            LoginId: data.loginId,
            AccessLevelSelection: data.accessLevelId || 0,
            ProjectSelection: data.projectNumber,
            DisciplineSelection: data.disciplineId,
            DD1: String(data.sequence ?? data.caCc ?? ''),
        });
    }

    useEffect(() => {
        if (uuid != null) {
            fetchAssignmentData();
        } else {
            createAssignmentData();
        }
    }, [uuid]);

    const { data: projects } = useQuery<Array<Project>, Error>({
        queryKey: ["projects"],
        queryFn: async () => {
            const response = await fetchApi(URLs.getProjectsUrl());
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const projectsData = await response.json();
            return projectsData.filter(project => !project.isArchived);
        },
        gcTime: 0,
    });

    const { allDisciplines: disciplines } = useSelector((state: RootState) => state.Discipline);
    const { AccessLevels: accessLevels } = useSelector((state: RootState) => state.AccessLevel);
    const projectSelectionWatch = useWatch({ control, name: "ProjectSelection" })
    const disciplineSelectionWatch = useWatch({ control, name: "DisciplineSelection" })
    const projectNumver = Number(projectSelectionWatch ?? assignmentData?.projectNumber)
    const disciplineId = Number(disciplineSelectionWatch ?? assignmentData?.disciplineId)
    const [showSequenceOptionsEmptyError, setShowSequenceOptionsEmptyError] = useState(false)

    const { data: DD1, refetch: refetchDD1 } = useQuery({
        queryKey: ["DD1"],
        queryFn: () => {
            return fetchApi(URLs.getDropDownListing(projectNumver, disciplineId)).then(res => res.json())
        },
        enabled: (!!projectSelectionWatch && !!disciplineSelectionWatch) || (!!assignmentData?.projectNumber && !!assignmentData?.disciplineId),
        gcTime: 0,
    })

    const showCACC = disciplineSelectionWatch === 1 || disciplineSelectionWatch === 6 || disciplineSelectionWatch === 7
    useEffect(() => {
        if (disciplineSelectionWatch && projectSelectionWatch &&
            (projectSelectionWatchValue.current !== projectSelectionWatch ||
                disciplineSelectionWatchValue.current !== disciplineSelectionWatch)) {
            refetchDD1().then(() => {
                setValue('DD1', '');
                setShowSequenceOptionsEmptyError(true);
            })
            disciplineSelectionWatchValue.current = disciplineSelectionWatch
            projectSelectionWatchValue.current = projectSelectionWatch
        }
    }, [disciplineSelectionWatch, projectSelectionWatch])

    const { mutateAsync, isPending: isMutating } = useMutation<any, Error, AssignmentApiData>({
        mutationKey: ["EditAssignment"],
        mutationFn: async (updatedData: AssignmentApiData) => {
            const url = uuid != null ? URLs.putAssignmentsUrl(updatedData.assignmentUuid) : URLs.postAssignmentsUrl();
            const method = uuid != null ? 'PUT' : 'POST';
            setLocalLoader(true)
            await fetchApi(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData),
            }).then(response => {
                if (!response.ok) {
                    response.json().then(errorResponse => {
                        setSaveFailedErrorMessage(errorResponse.detail);
                        setLocalLoader(false)
                    })
                }
            }).catch(err => {
                setSaveFailedErrorMessage(err);
                setLocalLoader(false)
            })
        },
    });

    const dispatch = useDispatch();
    const [localLoader, setLocalLoader] = useState(false);

    const handleDeleteUser = async () => {
        if (!uuid) return;

        try {
            setLocalLoader(true);
            const response = await fetchApi(URLs.deleteUserUrl(uuid), {
                method: 'DELETE'
            });

            if (response.ok) {
                navigate(-1); // Navigate back to the previous page
            } else {
                const errorData = await response.json();
                setSaveFailedErrorMessage(errorData.detail || 'Failed to delete user');
            }
        } catch (error) {
            setSaveFailedErrorMessage('An error occurred while deleting the user');
        } finally {
            setLocalLoader(false);
        }
    };

    const onSubmit = (formData: AssignmentFormType) => {
        if (!assignmentData.disciplineId && uuid) return
        const updatedData = {
            ...assignmentData,
            fullName: formData.FullName,
            kionEmail: formData.KionEmail,
            loginId: formData.LoginId,
            accessLevelId: formData.AccessLevelSelection,
            projectNumber: formData.ProjectSelection,
            disciplineId: formData.DisciplineSelection,
            sequence: formData.DD1,
            caCc: formData.DD1,
        };
        mutateAsync(updatedData).then(() => {
            setLocalLoader(true);
            fetchApi(URLs.getAssignmentUsingLoginIdUrl(Cookies.get(CookiesKeys.username)), { cache: 'no-store' })
                .then(res => res.json()).then((data: UserAssignment[]) => {
                    // A user can hold multiple assignment records, so `data[0]` is not
                    // guaranteed to be the one just edited. Prefer the record matching the
                    // edited assignmentUuid; fall back to the first entry only if not found.
                    const user = data.find(a => a.assignmentUuid === updatedData.assignmentUuid) ?? data[0];
                    const userProject = projects.find(p => p.projectNumber === user.projectNumber)?.projectName;
                    const userDiscipline = disciplines.find(d => d.disciplineId === user.disciplineId)?.disciplineName;
                    const userAccessLevel = accessLevels.find(al => al.accessLevelId === (user as any).accessLevelId)?.accessLevelName;
                    
                    // Use accessLevelName as role, or fallback to AD group for external users
                    let userRole = userAccessLevel;
                    if (!userRole) {
                        const rawRole = Cookies.get(CookiesKeys.roles);
                        const adGroupKey = ADGroupMapping[rawRole];
                        userRole = adGroupKey ? ROLES[adGroupKey] : 'User';
                    }
                    
                    const newUserData = {
                        ...user,
                        role: userRole,
                        projectNumber: user?.projectNumber,
                        disciplineId: user?.disciplineId,
                        sequence: user?.sequence,
                        project: userProject,
                        discipline: userDiscipline,
                        accessLevelName: userAccessLevel,
                        jwt: Cookies.get(CookiesKeys.jwt)
                    }
                    dispatch(addSelectedUser(newUserData));
                    if (!uuid) {
                        createAssignmentData()
                        navigate('/AllUsers'); // Navigate to users page after adding new user
                    } else {
                        navigate('/'); // Navigate to homepage after editing existing user
                    }
                    setLocalLoader(false)
                    // get the chained data
                    dispatch(updateDownloadTriggered({ key: "drawings", value: true }))
                })
        })
    };

    return (
        <>
            {localLoader || isMutating || isFetchingUserData ? <Loader loaders={{ isUpdatingUserDataLoading: localLoader, isFetchingUserData: isFetchingUserData }} /> :
                <div className='max-w-screen-md mt-4'>
                    <div className="mt-8 mb-8">
                        <SectionHeader title="User Details" />
                    </div>

                    <form className='relative' onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
                            e.preventDefault();
                        }
                    }}>
                        <FormInput
                            dataTestId="user-details-full-name-input"
                            required={true}
                            errors={errors}
                            defaultValue=""
                            inputKey="FullName"
                            registeredProps={register('FullName')}
                            label={splitOnCapitals("Last Name, First Name")}
                            placeholder={"Last Name, First Name"}
                            disabled={!canEditUserIdentity}
                        />
                        <FormInput
                            dataTestId="user-details-kion-email-input"
                            required={true}
                            errors={errors}
                            defaultValue=""
                            inputKey="KionEmail"
                            registeredProps={register('KionEmail')}
                            label="Email:"
                            placeholder={"first.last@kiongroup.com"}
                            disabled={!canEditUserIdentity}
                            helperText="KION / Dematic Email or External Email"
                        />
                        <FormInput
                            dataTestId="user-details-login-id-input"
                            required={true}
                            errors={errors}
                            defaultValue=""
                            inputKey="LoginId"
                            registeredProps={register('LoginId')}
                            label="Login ID:"
                            placeholder={"A00XXXXX@kiongroup.com"}
                            disabled={!canEditUserIdentity}
                            helperText="A00XXXXX or External Email"
                        />
                        <FormDDL
                            dataTestId="user-details-access-level-selection-dropdown"
                            required={true}
                            options={accessLevels?.filter(al => {
                                // Always exclude IT
                                if (al.accessLevelName === 'IT') return false;
                                // Only show SuperAdmin if current user is SuperAdmin or has canModifyRoles
                                if (al.accessLevelName === 'SuperAdmin' && !SelectedUser.canModifyRoles && userRole !== 'SuperAdmin') return false;
                                return true;
                            }).map(al => ({
                                text: al.accessLevelName,
                                value: al.accessLevelId,
                            })).sort((a, b) => {
                                if (a.text < b.text) {
                                    return -1;
                                } else if (a.text > b.text) {
                                    return 1;
                                }
                                return 0;
                            })}
                            control={control}
                            errors={errors}
                            defaultValue=""
                            inputKey="AccessLevelSelection"
                            label="Access Level / Role"
                            name="AccessLevelSelection"
                            disabled={!canEditUserIdentity}
                        />
                        <FormDDL
                            dataTestId="user-details-project-selection-dropdown"
                            required={true}
                            options={projects?.sort((a, b) => {
                                if (a.projectName < b.projectName) {
                                    return -1;
                                } else if (a.projectName > b.projectName) {
                                    return 1;
                                }
                                return 0;
                            }).map(p => ({
                                text: getProjectText(p),
                                value: p.projectNumber,
                            }))}
                            control={control}
                            errors={errors}
                            defaultValue=""
                            inputKey="ProjectSelection"
                            label="Project Selection"
                            name="ProjectSelection"
                            disabled={!canEditUserAssignment && !canEditOwnProject}
                        />
                        <FormDDL
                            dataTestId="user-details-discipline-selection-dropdown"
                            required={true}
                            options={disciplines?.map(d => ({
                                text: d.disciplineName as string,
                                value: d.disciplineId as number,
                            })).sort((a, b) => {
                                if (a.text < b.text) {
                                    return -1;
                                } else if (a.text > b.text) {
                                    return 1;
                                }
                                return 0;
                            })}
                            control={control}
                            errors={errors}
                            defaultValue=""
                            inputKey="DisciplineSelection"
                            label="Discipline Selection"
                            name="DisciplineSelection"
                            disabled={!canEditUserAssignment && !canEditOwnDiscipline}
                        />
                        <FormDDL
                            dataTestId="user-details-dd1-selection-dropdown"
                            isMultiSelect={true}
                            required={false}
                            options={(DD1?.length ? DD1.sort() : [])?.map(s => ({
                                text: String(s),
                                value: s
                            }))}
                            control={control}
                            errors={errors}
                            defaultValue={''}
                            inputKey={"DD1"}
                            label={showCACC ? "CACC Selection" : "Sequence Selection"}
                            name={"DD1"}
                            closeMenuOnSelect={false}
                            selectRef={dd1SelectRef}
                            onChangeEvent={(selectedArray) => {
                                const values = getValues()
                                schema.safeParse(values);
                                setValue('DD1', selectedArray.join(','));

                                if (selectedArray.length === 10 && dd1SelectRef.current) {
                                    dd1SelectRef.current.blur();
                                }
                            }}
                            showErrorIfOptionsEmpty={showSequenceOptionsEmptyError}
                            errorTextIfOptionsIsEmpty={`The project and discipline selected have no Sequences/Areas available`}
                            disabled={!canEditUserAssignment && !canEditOwnSequence} />

                        <div>
                            <p hidden={saveFailedErrorMessage == null} style={{ color: "red" }}>
                                {saveFailedErrorMessage}<br />
                                Please ask support or try again later.
                            </p>

                        </div>
                        <div className='flex items-center mt-16'>
                            <button type="button" onClick={() => navigate(-1)}
                                className='absolute left-0 mt-3 bg-gray-500  dark:bg-gray-500'>Cancel
                            </button>
                            <button type="submit"
                                className='absolute right-0 bg-orange-1000 dark:bg-orange-1000'>Save
                            </button>
                        </div>
                    </form>

                    {canEditUserIdentity && assignmentData?.assignmentUuid && (
                        <div className='flex justify-center mt-20'>
                            <button
                                onClick={() => dispatch(setModal({
                                    open: true,
                                    Chilren: () => (
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2">Confirm Delete</h3>
                                            <p>Are you sure you want to delete this user?</p>
                                            <p> This action cannot be undone.</p>
                                        </div>
                                    ),
                                    onConfirm: handleDeleteUser,
                                }))}
                                className='text-white bg-gray-500 hover:bg-red-500 px-4 py-2 rounded transition-colors duration-200'
                            >
                                Delete User
                            </button>
                        </div>
                    )}
                </div>
            }
        </>
    );
}
